<template>
    <tr>
        <td></td>
        <td>
            <div class="cm-crops" data-testid="cm-crops-section">
                <div
                    v-if="showPredictedPreviews"
                    class="cm-pred-previews"
                    data-testid="cm-pred-previews"
                >
                    <div class="cm-pred-previews__title">
                        {{ $t('confusion_matrix.predicted_as') }}:
                        <strong>{{ predictedLabel }}</strong>
                    </div>
                    <div class="cm-pred-previews__row">
                        <div
                            v-for="(preview, idx) in predPreviews"
                            :key="'pred-' + idx + '-' + (preview.asset_id || idx)"
                            class="cm-pred-preview"
                            data-testid="cm-pred-preview"
                            @click="openAnnotateFromPreview(preview)"
                        >
                            <img
                                v-if="preview.data"
                                :src="'data:image/png;base64, ' + preview.data"
                                alt=""
                            />
                            <div v-else class="cm-pred-preview__placeholder">
                                {{ $t('confusion_matrix.preview_unavailable') }}
                            </div>
                        </div>
                    </div>
                </div>

                <div v-else class="cm-crops__pred-title">
                    <span v-if="type === 'confused_with'">
                        {{ $t('confusion_matrix.predicted_as') }}:
                    </span>
                    {{ title }}
                </div>

                <div style="padding-bottom: 20px;">
                    <grid
                        ref="grid"
                        :model_id="model_id"
                        :options="[]"
                        :no_toggle="true"
                        :grid_size="gridSize"
                        :items="crops"
                        :show_model_correct_button="showModelCorrectButton"
                        @model_correct="onModelCorrect"
                    ></grid>
                </div>
                <div style="padding-bottom: 20px;" v-if="crops.length < normalizedItems.length">
                    <v-btn color="primary" @click="load_more_crops">Load more</v-btn>
                </div>
            </div>

            <annotate-modal
                v-if="showAnnotateModal && annotateAssetId"
                data-testid="cm-annotate-modal"
                @close_modal="closeAnnotateModal"
                :asset_id="annotateAssetId"
            ></annotate-modal>
        </td>
    </tr>
</template>

<script>
  import Grid from "@/components/Grid";
  import AnnotateModal from "@/components/AnnotateModal";
  import Annotation from "@/classes/annotation";
  import * as annotateModalGuard from '@/utils/annotateModalGuard';

  export default {
    name: "Crops",
    components: {Grid, AnnotateModal},
    props: {
      model_id: {required: true},
      items: {
        type: Array,
        default: () => [],
      },
      title: {required: true},
      type: {required: true},
      viewMode: {
        type: String,
        default: 'crops',
      },
      gridSize: {
        type: Number,
        default: 4,
      },
    },
    emits: ['prediction_accepted', 'model_results_updated'],
    data () {
      return {
        loading: false,
        crops: [],
        loadSeq: 0,
        predPreviews: [],
        predLoadSeq: 0,
        showAnnotateModal: false,
        annotateAssetId: null,
        acceptingBboxId: null,
      }
    },
    computed: {
      normalizedItems() {
        return Array.isArray(this.items) ? this.items : [];
      },
      predictedLabel() {
        if (this.title && this.title !== this.type) {
          return this.title;
        }
        const first = this.normalizedItems.find((item) => item.pred_label_name);
        return first ? first.pred_label_name : (this.title || '');
      },
      showPredictedPreviews() {
        return this.type === 'confused_with' && Boolean(this.predictedLabel);
      },
      showModelCorrectButton() {
        return this.type === 'confused_with';
      },
    },
    watch: {
      viewMode() {
        this.reloadVisibleItems();
      },
      items() {
        this.reloadVisibleItems();
        this.loadPredictedPreviews();
      },
      title() {
        this.loadPredictedPreviews();
      },
    },
    methods: {
      openAnnotateFromPreview(preview) {
        const assetId = preview && (preview.asset_id || preview.id);
        if (!annotateModalGuard.canOpenAnnotateModal()) {
          return;
        }
        if (!assetId || this.showAnnotateModal) {
          return;
        }
        this.annotateAssetId = assetId;
        this.showAnnotateModal = true;
      },
      closeAnnotateModal() {
        annotateModalGuard.blockAnnotateModalOpen();
        this.annotateAssetId = null;
        this.showAnnotateModal = false;
      },
      async loadPredictedPreviews() {
        if (!this.showPredictedPreviews) {
          this.predPreviews = [];
          return;
        }
        const seq = ++this.predLoadSeq;
        const projectId = sessionStorage.getItem('project_id');
        const exclude = this.normalizedItems
          .map((item) => item.bbox_id)
          .filter((id) => id != null);
        try {
          const response = await this.$api.label.get_random_crops(
            this.predictedLabel,
            projectId,
            4,
            exclude,
          );
          if (seq !== this.predLoadSeq) {
            return;
          }
          const crops = (response && response.crops) || [];
          this.predPreviews = crops.map((crop) => ({
            data: crop.crop || null,
            asset_id: crop.asset_id,
            bbox_id: crop.bbox_id,
            id: crop.asset_id,
          }));
          while (this.predPreviews.length < 4) {
            this.predPreviews.push({ data: null, asset_id: null, bbox_id: null });
          }
        } catch (e) {
          if (seq !== this.predLoadSeq) {
            return;
          }
          this.predPreviews = [
            { data: null }, { data: null }, { data: null }, { data: null },
          ];
        }
      },
      reloadVisibleItems() {
        const seq = ++this.loadSeq;
        const items = this.normalizedItems;
        const previousCount = this.crops.length;
        const count = previousCount > 0
          ? previousCount
          : Math.min(items.length, 4);
        this.crops = [];
        for (let i = 0; i < Math.min(count, items.length); i++) {
          this.load_crop(items[i], seq);
        }
      },
      buildGridItem(sourceItem, cropEntry) {
        const assetId = cropEntry.asset_id || sourceItem.asset_id;
        const label = sourceItem.label_name || sourceItem.pred_label_name || cropEntry.label || '';
        const base = {
          id: assetId,
          asset_id: assetId,
          bbox_id: sourceItem.bbox_id || cropEntry.bbox_id,
          meta: sourceItem.meta,
          validation: '',
          label,
          certainty: sourceItem.conf != null ? String(sourceItem.conf) : '',
          translation: sourceItem.pred_label_name || '',
          pred_label_id: sourceItem.pred_label_id,
          pred_label_name: sourceItem.pred_label_name,
          label_id: sourceItem.label_id,
          label_name: sourceItem.label_name,
        };

        if (this.viewMode === 'full') {
          const coords = cropEntry.coords || sourceItem.bbox;
          const annTypeNum = cropEntry.annotation_type != null
            ? cropEntry.annotation_type
            : (Array.isArray(coords) && coords.length === 2 ? 2 : 1);
          const annType = annTypeNum === 2 ? 'point' : 'box';
          const format = cropEntry.format || (annType === 'point' ? 'normalized_xy' : 'normalized_xywh');
          const path = cropEntry.path || `/protected/asset/${assetId}`;
          base.image_url = path.startsWith('http') ? path : (this.$api_host + path);
          base.annotation_type = annTypeNum;
          if (Array.isArray(coords) && coords.length > 0) {
            base.annotations = [
              new Annotation(
                base.bbox_id || `cm-${assetId}`,
                annType,
                coords,
                base.certainty,
                label,
                [249, 65, 68],
                true,
                {},
                format,
              ),
            ];
          } else {
            base.annotations = [];
          }
          return base;
        }

        base.data = cropEntry.crop;
        base.annotation_type = cropEntry.annotation_type;
        return base;
      },
      async load_crop(item, seq = null) {
        if (seq == null) {
          seq = ++this.loadSeq;
        }
        let cropEntry = null;
        try {
          const crop_data = await this.$api.asset.get_crop_for_conf_items(this.type, JSON.stringify([item]));
          if (crop_data.crops && crop_data.crops.length > 0) {
            cropEntry = crop_data.crops[0];
          }
        } catch (e) {
          cropEntry = null;
        }

        if (seq !== this.loadSeq) {
          return;
        }

        if (!cropEntry) {
          const aid = item.asset_id;
          cropEntry = {
            crop: false,
            asset_id: aid,
            bbox_id: item.bbox_id,
            coords: item.bbox,
            path: aid ? `/protected/asset/${aid}` : '',
            annotation_type: Array.isArray(item.bbox) && item.bbox.length === 2 ? 2 : 1,
          };
        }

        this.crops.push(this.buildGridItem(item, cropEntry));
      },
      load_more_crops() {
        const seq = this.loadSeq;
        const items = Array.isArray(this.items) ? this.items : [];
        for (let i = this.crops.length; i < Math.min(this.crops.length + 4, items.length); i++) {
          this.load_crop(items[i], seq);
        }
      },
      async onModelCorrect(bboxId) {
        if (!bboxId || this.acceptingBboxId) {
          return;
        }
        this.acceptingBboxId = bboxId;
        if (this.$refs.grid && this.$refs.grid.setModelCorrectBusyBboxId) {
          this.$refs.grid.setModelCorrectBusyBboxId(bboxId);
        }
        try {
          const response = await this.$api.conf_matrix_item.accept_prediction(this.model_id, bboxId);
          this.crops = this.crops.filter((crop) => crop.bbox_id !== bboxId);
          this.$emit('prediction_accepted', {
            bbox_id: bboxId,
            response,
          });
        } catch (e) {
          console.error('Failed to accept model prediction', e);
          this.$store.dispatch(
            'snackbars/add_failed_snack',
            (e && e.message) || 'Failed to update ground truth label',
          );
        } finally {
          this.acceptingBboxId = null;
          if (this.$refs.grid && this.$refs.grid.setModelCorrectBusyBboxId) {
            this.$refs.grid.setModelCorrectBusyBboxId(null);
          }
        }
      },
    },
    mounted() {
      this.reloadVisibleItems();
      this.loadPredictedPreviews();
    }
  }
</script>

<style scoped>
.cm-pred-previews {
  margin-bottom: 16px;
  padding: 12px;
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.cm-pred-previews__title {
  font-size: 0.95rem;
  color: #444;
  margin-bottom: 10px;
}
.cm-pred-previews__row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.cm-pred-preview {
  width: calc(25% - 8px);
  min-width: 90px;
  max-width: 160px;
  height: 100px;
  border: 1px solid #ccc;
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cm-pred-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.cm-pred-preview__placeholder {
  color: #999;
  font-size: 0.75rem;
  text-align: center;
  padding: 8px;
}
.cm-crops__pred-title {
  margin-bottom: 8px;
  font-weight: 500;
}
</style>
